import { domainEventsApi, parseDomainEventFeed } from '#entities';
import {
  getSeq,
  REACT_APP_BE_API_URL,
  reportThrottled,
  seedSeq,
} from '#shared';

import {
  fetchWithAuthRecovery,
  isReportedAuthError,
  recoverFromAuthFailure,
} from '#shared/api';
import type { AuthTokenGetter } from '#shared/api';

import { applyDomainEventFrame } from './applyDomainEventFrame';

import type { AppDispatch } from '#shared';

export const REPORT_THROTTLE_MS = 60_000;
const EVENTS_PATH = 'api/events';
// Backstop so a huge gap can't loop forever; the BE caps each page at 2000 rows.
export const MAX_PAGES = 100;

// The reconnect drain and the history backfill page through this; cold start
// fetches through RTK Query instead (see below).
export const buildSinceUrl = (sinceSeq: string): string =>
  `${REACT_APP_BE_API_URL ?? ''}${EVENTS_PATH}?sinceSeq=${sinceSeq}`;

// Goes through the shared recovery so a mid-session expiry is refreshed and
// retried in place rather than ending the drain — this raw-fetch path was
// invisible to the RTK Query base query's recovery.
export const fetchFeed = async (
  url: string,
  token: string,
  refreshToken: AuthTokenGetter,
) => {
  const res = await fetchWithAuthRecovery(url, token, { refreshToken });
  if (!res.ok) {
    throw new Error(`nop-events: domain-event page failed (${res.status})`);
  }
  return parseDomainEventFeed(await res.json());
};

/**
 * Reconnect entry point (from `socket.onopen`). Cold start (no cursor): fetch the
 * lookback window through RTK Query so it dedupes/coordinates with
 * `EventsFeedProvider`'s own `getDomainEvents` subscription, then seed the cursor.
 * Reconnect: page `?sinceSeq=` until the last id reaches `latestSeq`, applying
 * each frame idempotently. Best-effort, never throws.
 */
export const runDomainEventCatchup = async (
  dispatch: AppDispatch,
  getToken: AuthTokenGetter,
): Promise<void> => {
  try {
    const token = await getToken();
    if (!token) {
      reportThrottled(
        'nop-events:de-catchup-no-token',
        'nop-events: domain-event catch-up skipped, no auth token',
        { throttleMs: REPORT_THROTTLE_MS, level: 'warning' },
      );
      return;
    }

    if (getSeq() === '') {
      // Cold start — fetch the lookback window THROUGH RTK Query so it
      // dedupes/coordinates with the `getDomainEvents` subscription that
      // `EventsFeedProvider` already holds. A raw fetch + `upsertQueryData`
      // here would race that subscription's own fetch: `upsertQueryData`
      // clobbers the whole cache entry, so a live frame applied between the
      // two uncoordinated fetches could be silently overwritten while the
      // cursor had already advanced past it. `initiate` (subscribe:false)
      // reuses an in-flight request instead of starting a second one, writes
      // the parsed feed into the same cache entry, and `.unwrap()` rethrows
      // any error into the outer catch. Then seed the cursor.
      //
      // TMDXNC-54: the cache is keyed by relevance view, so cold start seeds
      // whichever views the app has actually subscribed to — initiating a bare
      // `{}` (no view) would pull the FULL feed even for a `view=case` session,
      // which is the transfer this ticket exists to avoid. Falls back to
      // `{}` when the socket opens before any page has mounted its provider;
      // that entry then serves whoever asks for the unfiltered feed, and a
      // view-scoped provider fetches its own on mount.
      //
      // `latestSeq` is the GLOBAL tail in every view (the BE reports store
      // bounds, not filtered ones), so seeding from any of them is equivalent.
      const cachedArgs = dispatch((_d, getState) =>
        domainEventsApi.util.selectCachedArgsForQuery(
          getState(),
          'getDomainEvents',
        ),
      );
      const coldStartArgs = cachedArgs.length > 0 ? cachedArgs : [{}];
      const feeds = await Promise.all(
        coldStartArgs.map(arg =>
          dispatch(
            domainEventsApi.endpoints.getDomainEvents.initiate(arg, {
              subscribe: false,
            }),
          ).unwrap(),
        ),
      );
      seedSeq(feeds[0].latestSeq);
      return;
    }

    // Reconnect — drain forward by seq.
    //
    // TMDXNC-14 degradation signals live in this loop. Note what is NOT here: a
    // retention-gap check. The only client-visible evidence would be
    // non-contiguous ids, and id gaps are perfectly normal — the BE's
    // ON CONFLICT DO NOTHING insert still consumes sequence values — so such a
    // check would fire on ordinary duplicate suppression. Detecting a real gap
    // needs the retained floor, which only the server knows; it reports it as
    // domain_event_feed_gap. Please do not "fix" this omission.
    // Kept for the not-converging report below: without it you learn the cursor
    // but not how far behind it is, which is the whole severity of the signal.
    let lastLatestSeq = '';
    for (let page = 0; page < MAX_PAGES; page += 1) {
      const seqBefore = getSeq();
      const feed = await fetchFeed(buildSinceUrl(seqBefore), token, getToken);
      lastLatestSeq = feed.latestSeq;
      for (const frame of feed.events) {
        dispatch(applyDomainEventFrame(frame));
      }
      const reachedTail =
        feed.events.length === 0 || getSeq() === feed.latestSeq;
      if (reachedTail) {
        return;
      }
      // Resync loop: the server handed us rows, yet the cursor did not move at
      // all. Distinct from converging slowly — this one cannot make progress, so
      // the remaining pages are guaranteed to repeat. Bail rather than spin.
      if (getSeq() === seqBefore) {
        reportThrottled(
          'nop-events:de-catchup-stalled',
          'nop-events: domain-event catch-up stalled, cursor did not advance',
          {
            throttleMs: REPORT_THROTTLE_MS,
            level: 'warning',
            extra: {
              cursor: seqBefore,
              latestSeq: feed.latestSeq,
              received: feed.events.length,
            },
          },
        );
        return;
      }
    }
    // Not converging: the loop ran out of pages with the server still
    // advertising a tail ahead of our cursor. This is the highest-value
    // degradation signal because it cannot false-positive on a quiet feed — the
    // server itself is saying events exist that this client does not have.
    reportThrottled(
      'nop-events:de-catchup-truncated',
      'nop-events: domain-event catch-up hit page cap without reaching the tail',
      {
        throttleMs: REPORT_THROTTLE_MS,
        level: 'warning',
        extra: { cursor: getSeq(), latestSeq: lastLatestSeq, pages: MAX_PAGES },
      },
    );
  } catch (error) {
    if (isReportedAuthError(error)) {
      // An auth failure. It already emitted its own deduped event naming the
      // owner, so re-reporting it as a generic catch-up failure would only bury
      // the reason. Hand it to the shared guarded handlers instead of going quiet
      // (TMDXNC-52) — a session loss re-authenticates, a 403 routes to no-access.
      recoverFromAuthFailure(error);
      return;
    }
    reportThrottled(
      'nop-events:de-catchup-failed',
      'nop-events: domain-event catch-up failed',
      {
        throttleMs: REPORT_THROTTLE_MS,
        level: 'warning',
        extra: {
          error: error instanceof Error ? error.message : String(error),
        },
      },
    );
  }
};
